<template>
    <page-wrapper
        :class="{ 'layout-primary--with-pagination': $slots.pagination }"
        :is-scroll-disabled="isScrollDisabled"
        class="layout-primary"
    >
        <page-header slot="pageHeader"
                     @closeNav="enableScroll"
        />

        <main slot="pageContent">
            <modals-container />
            <global-toasts />

            <div v-if="showInnerHeader"
                 class="inner-header"
                 :class="{ noInnerPadding: titleText === '' }"
            >
                <div ref="fixedHeaderId"
                     :class="{ 'is-active': fixedHeader.fixedActive }"
                     class="inner-header__row fixed-page-header"
                >
                    <mobile-nav-toggle @open="disableScroll" />

                    <h1 class="inner-header__title">
                        {{ titleText }}
                    </h1>

                    <mq-layout v-if="$slots.titleAction"
                               mq="tablet+"
                               class="inner-header__title-action"
                    >
                        <slot name="titleAction" />
                    </mq-layout>

                    <div v-if="$slots.headerRight"
                         class="inner-header__search"
                    >
                        <slot name="headerRight" />
                    </div>
                </div>
                <div class="inner-header__row">
                    <mobile-nav-toggle @open="disableScroll" />

                    <h1 class="inner-header__title">
                        {{ titleText }}
                    </h1>

                    <mq-layout v-if="$slots.titleAction"
                               mq="tablet+"
                               class="inner-header__title-action"
                    >
                        <slot name="titleAction" />
                    </mq-layout>

                    <div v-if="$slots.headerRight"
                         class="inner-header__search"
                    >
                        <slot name="headerRight" />
                    </div>
                </div>

                <div v-if="$slots.headerContent"
                     class="inner-header__row"
                >
                    <slot name="headerContent" />
                </div>
            </div>

            <spinner :active="isActive && !disableSpinner"
                     full-screen
            />

            <div v-if="$slots.notification">
                <slot name="notification" />
            </div>

            <div class="layout-primary__content">
                <div class="layout-primary__main-content">
                    <div v-if="$slots.preContent"
                         class="layout-primary__filter"
                    >
                        <slot name="preContent" />
                    </div>

                    <slot />

                    <div v-if="$slots.pagination"
                         class="layout-primary__pagination"
                    >
                        <slot name="pagination" />
                    </div>
                </div>

                <mq-layout v-if="$slots.sideContent"
                           mq="nav+"
                           class="layout-primary__side-content"
                >
                    <slot name="sideContent" />
                </mq-layout>
            </div>

            <base-icon-button ref="backToTop"
                              :class="{ 'is-active': fixedHeader.fixedActive }"
                              primary
                              size="lg"
                              with-shadow
                              title-text="$t('general.back')"
                              class="fixed-page-header__back-to-top"
                              @click="() => fixedHeader.scrollToTop()"
            >
                <svg>
                    <use xlink:href="#chevron-up" />
                </svg>
            </base-icon-button>
        </main>
    </page-wrapper>
</template>

<script>
import '@/assets/icons/chevron-up.svg';
import { mapGetters } from 'vuex';
import PageHeader from '@/components/layout/PageHeader';
import { GlobalToasts } from '@/components/notifications';
import Spinner from '@/components/Spinner';
import MobileNavToggle from '@/components/layout/MobileNavToggle';
import PageWrapper from '@/components/layout/PageWrapper';
import MobileFixedHeader from '@/core/layout/mobileFixedHeader';
import BaseIconButton from '@/components/base/BaseIconButton';

export default {
    name: 'LayoutPrimary',
    components: {
        BaseIconButton,
        PageWrapper,
        MobileNavToggle,
        PageHeader,
        GlobalToasts,
        Spinner,
    },
    props: {
        titleText: {
            type: String,
            required: true,
        },
        disableSpinner: {
            type: Boolean,
            default: false,
        },
        showInnerHeader: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            fixedHeader: new MobileFixedHeader(),
            isScrollDisabled: false,
            scrolledAmount: 0,
        };
    },
    computed: {
        ...mapGetters('spinner', ['isActive']),
    },
    mounted() {
        this.fixedHeader.init(this.$refs.fixedHeaderId, this.$refs.backToTop);
    },
    destroyed() {
        this.fixedHeader.destroy();
    },
    methods: {
        disableScroll() {
            this.scrolledAmount = (window.pageYOffset !== undefined) ? window.pageYOffset
                : (document.documentElement || document.body.parentNode || document.body).scrollTop;
            this.isScrollDisabled = true;
        },
        enableScroll() {
            this.isScrollDisabled = false;
            // this is needed, so the scroll event would definitely be fired later than the sidebar animation event
            setTimeout(() => {
                window.scrollTo({
                    left: 0,
                    top: this.scrolledAmount,
                });
            }, 5);
        },
    },
};
</script>

<style scoped lang="scss">
.layout-primary {
    $_this: &;

    min-height: 100%;
    background-color: $color-background-light-3;

    &__content {
        display: block;
    }

    &__main-content {
        position: relative;
        max-width: 100%;
    }

    &__filter {
        padding: 0 $page-padding-sides 20px;

        ::v-deep .col {
            flex-basis: auto;
        }
    }

    &--with-pagination {
        @include sticky-footer('#{$_this}__pagination', $pagination-height);
    }

    @include respond-to($media-desktop + 1, min) {
        &__content {
            display: flex;
        }

        &__main-content {
            flex: 1;
        }

        &__side-content {
            width: 400px;
            min-width: 260px;
            max-width: 400px;
            padding: 20px 0 20px 20px;
        }
    }

    @include respond-to($media-tablet, min) {
        &__main-content {
            padding: 20px 0 1px;
        }
    }

    @include respond-to($media-mobile, max) {
        &__filter {
            padding: $page-padding-sides-mobile*.75 $page-padding-sides-mobile;
        }
    }
}

.inner-header {
    padding: 12px $page-padding-sides 20px;
    background-color: $color-background-dark;
    box-shadow: $box-shadow-page-header;
    color: $color-white;

    &__row {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    &__title {
        margin: 0;
        color: inherit;
    }

    &__title-action {
        position: relative;
        top: 1px;
        left: 3px;
        margin-left: 10px;
    }

    &__search {
        margin-left: auto;
    }

    @include respond-to($media-mobile-nav, max) {
        padding-top: 16px;
    }

    @include respond-to($media-mobile, max) {
        padding: 4px $page-padding-sides-mobile 3px;

        &__title {
            font-family: $font-family-primary;
            font-size: 16px;
        }
    }
}

.noInnerPadding {
    padding: 0 0;
    padding-left: 10px;
}

.fixed-page-header {
    @include mobileFixedPageHeader;
}
</style>
